import { callApi, stripTrailingSlash } from '@wyz/utils';
import { ISuccessApiResponse } from '@wyz/types';
import {
  ApplicationConfigResponse,
  BootstrapConfig,
  LegacyBootstrapConfig,
} from '~/common/types';
import { defaultLogger } from '~/common/helpers';

type Call = ReturnType<typeof callApi>;

type ViteEnv = {
  BASE_URL: string;
  VITE_API_URL?: string;
};

const env = import.meta.env as unknown as ViteEnv;

const API_URL_PLACEHOLDER = '__WYZ_API_URL__';
const USETIFUL_ENVIRONMENTS = ['prod', 'preprod'];

export function getApiUrl(): string {
  const injected = window.__API_URL__;
  if (injected && injected !== API_URL_PLACEHOLDER) {
    return stripTrailingSlash(injected);
  }
  return stripTrailingSlash(env.VITE_API_URL ?? '');
}

function isNotFound(error: unknown): boolean {
  return (
    (error as { _response?: { status?: number } })?._response?.status === 404
  );
}

async function fromEndpoint(call: Call): Promise<BootstrapConfig> {
  const apiUrl = getApiUrl();
  const response = await call<ISuccessApiResponse<ApplicationConfigResponse>>(
    `${apiUrl}/tenants/api/config`,
    { authless: true },
  );
  const data = response.data;

  return {
    apiUrl,
    appCdnUrl: stripTrailingSlash(env.BASE_URL),
    cdnUrl: data.cdnUrl,
    tenant: data.tenant,
    tenantId: data.tenantId,
    tenantName: data.tenantName,
    environment: data.environment,
    authority: data.authority,
    clientId: data.clientId,
    wyzFlowApiUrl: data.wyzFlowApiUrl,
    routingBasename: data.routingBasename,
    usetifulEnabled: data.usetifulEnabled,
    usetifulToken: data.usetifulToken,
    gtmToken: data.gtmToken,
    matomoUrl: data.matomoUrl,
  };
}

// TODO(WCT-4865): remove fromFile and its fallback once every tenant is synced.
async function fromFile(call: Call): Promise<BootstrapConfig> {
  const configUrl = `${window.__BASE_URL__ ?? '/'}config.json`;
  const data = await call<LegacyBootstrapConfig>(configUrl, { authless: true });

  return {
    apiUrl: data.apiUrl,
    appCdnUrl: stripTrailingSlash(data.appCdnUrl ?? ''),
    cdnUrl: data.cdnUrl,
    tenant: data.tenant,
    tenantId: data.tenantId,
    tenantName: data.tenantName,
    environment: data.environment,
    authority: data.authority,
    clientId: data.clientId,
    wyzFlowApiUrl: data.wyzFlowApiUrl,
    routingBasename: data.routingBasename,
    usetifulEnabled: USETIFUL_ENVIRONMENTS.includes(data.environment),
    usetifulToken: data.usetifulToken,
    gtmToken: data.gtmToken,
    matomoUrl: data.matomoUrl,
  };
}

export async function loadBootstrapConfig(
  call: Call,
): Promise<BootstrapConfig> {
  try {
    return await fromEndpoint(call);
  } catch (error) {
    if (!isNotFound(error)) {
      defaultLogger.warn(
        'Config endpoint failed, falling back to config.json',
        error,
      );
    }
    return fromFile(call);
  }
}
